import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'

interface VideoDashboardViewProps {
  name: string
  description?: string
  /** The MP4 stream the player loads. */
  src: string
  /** The same stream with `download=1`, which the server sends as an attachment. */
  downloadHref: string
  fileName: string
  /** Absent on a shared link, which has nowhere to go back to. */
  onBack?: () => void
  /** Extra buttons in the top bar, e.g. Duplicate. */
  topBarExtra?: React.ReactNode
}

/**
 * A video DataViz item (type `video`): the chat's MP4 on a player with a
 * Download MP4 button. There is no editor; the owner changes it by publishing
 * again from the chat. Used by the DataViz viewer and by shared links.
 */
const VideoDashboardView: React.FC<VideoDashboardViewProps> = ({
  name,
  description,
  src,
  downloadHref,
  fileName,
  onBack,
  topBarExtra,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'white' }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {onBack ? (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">DataViz</Button>
      ) : (
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{name}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {topBarExtra}
        <Button
          component="a"
          href={downloadHref}
          download={fileName}
          variant="contained"
          color="info"
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
          data-testid="video-download"
          sx={{ textTransform: 'none' }}
        >
          Download MP4
        </Button>
      </Box>
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 1100, backgroundColor: '#000', borderRadius: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
        <video
          controls
          preload="metadata"
          src={src}
          data-testid="dashboard-video"
          style={{ display: 'block', width: '100%', maxHeight: 'calc(100vh - 220px)', background: '#000' }}
        />
      </Box>
      <Box sx={{ width: '100%', maxWidth: 1100 }}>
        {onBack && <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{name}</Typography>}
        {description && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>{description}</Typography>
        )}
      </Box>
    </Box>
  </Box>
)

export default VideoDashboardView
